// ============================================================
// CreateSecureAccess — /olivium/secure-access/new.
//
// Issue a real controlled access code (sq_access_codes) bound to a target. The
// one-time plaintext is returned ONCE and shown behind a "copy now" warning —
// never stored, never shown again. A bound target requires a target id ("no
// code without a destination"). PHASE_2L target types issue only once migration
// 20260710000001 is applied; otherwise the API rejects honestly.
// ============================================================

import { useState } from 'react'
import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import { OLIVIUM_ACCENT } from '../portal-factory/portalFactory'
import { SectionCard, StateBlock, chipBtn, primaryBtn, inputStyle, fieldLabel } from '../portal-factory/PortalKit'
import { SECURE_TARGET_TYPES, type IssuePayload, type ExpiresIn } from './secureAccessData'
import type { AccessCodeTargetType, CommandCenterAccessCode } from '../../../../api/client'

export interface CreateSecureAccessProps {
  saving?: boolean
  error?: string | null
  authRequired?: boolean
  forbidden?: boolean
  issued?: { access_code: string; code: CommandCenterAccessCode; message: string } | null
  /** PHASE_2M P7 — prefill (e.g. arriving from a portal's "Issue secure access code"). */
  initialLabel?: string
  initialTargetType?: AccessCodeTargetType
  initialTargetId?: string
  initialTargetLabel?: string
  onHome?: () => void
  onBackCommandCenter?: () => void
  onOpenHub?: () => void
  onSubmit?: (payload: IssuePayload) => void
}

const AUDIENCES: IssuePayload['audience'][] = ['operator', 'team', 'client']
const ROLES: IssuePayload['role'][] = ['operator', 'client_owner', 'client_collaborator', 'viewer']
const EXPIRES: ExpiresIn[] = ['1h', '24h', '7d', '30d', 'never']

export default function CreateSecureAccess(props: CreateSecureAccessProps) {
  const { saving, error, authRequired, forbidden, issued, onHome, onBackCommandCenter, onOpenHub, onSubmit } = props
  const [label, setLabel] = useState(props.initialLabel ?? '')
  const [audience, setAudience] = useState<IssuePayload['audience']>('client')
  const [role, setRole] = useState<IssuePayload['role']>('viewer')
  const [maxUses, setMaxUses] = useState('1')
  const [expiresIn, setExpiresIn] = useState<ExpiresIn>('7d')
  const [targetType, setTargetType] = useState<'' | AccessCodeTargetType>(props.initialTargetType ?? '')
  const [targetId, setTargetId] = useState(props.initialTargetId ?? '')
  const [targetLabel, setTargetLabel] = useState(props.initialTargetLabel ?? '')

  const canSubmit = label.trim().length > 0 && (!targetType || targetId.trim().length > 0) && !saving

  // Success — the one-time reveal.
  if (issued) {
    return (
      <CommandCenterShell onHome={onHome} onBackCommandCenter={onBackCommandCenter} parentBack={{ label: '← Secure Access', onClick: onOpenHub }} breadcrumb={['OLIVIUM', 'SECURE ACCESS', 'ISSUED']} title="Access code issued" titleAccent="#059669">
        <SectionCard title="Copy this code now — it is shown once" accent="#b8862a">
          <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', fontFamily: CC_MONO, fontSize: 15, fontWeight: 800, color: '#e0b050', letterSpacing: '0.04em', wordBreak: 'break-all' }}>{issued.access_code}</div>
          <p style={{ margin: '10px 0 0', fontSize: 11, color: CC_INK.muted }}>{issued.message}</p>
          <p style={{ margin: '6px 0 0', fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>Stored hash-only (sha256). Prefix {issued.code.code_prefix} · target {issued.code.target_label || issued.code.target_type || 'unbound'}.</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" style={primaryBtn} onClick={onOpenHub}>Done → Secure Access</button>
          </div>
        </SectionCard>
      </CommandCenterShell>
    )
  }

  return (
    <CommandCenterShell onHome={onHome} onBackCommandCenter={onBackCommandCenter} parentBack={{ label: '← Secure Access', onClick: onOpenHub }} breadcrumb={['OLIVIUM', 'SECURE ACCESS', 'NEW']} title="Issue secure access" titleAccent={OLIVIUM_ACCENT}>
      <div>
        {authRequired && <div style={{ marginBottom: 14 }}><StateBlock kind="auth" title="Sign in to issue access" /></div>}
        {forbidden && <div style={{ marginBottom: 14 }}><StateBlock kind="error" title="Operator access required" body="Issuing access codes is admin-gated (minLevel 80)." /></div>}
        {error && <div style={{ marginBottom: 14 }}><StateBlock kind="error" title="Could not issue access" body={error} /></div>}

        <SectionCard title="Code">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ gridColumn: '1 / -1' }}><span style={fieldLabel}>Label *</span><input style={inputStyle} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Silver Key repo review" /></label>
            <label><span style={fieldLabel}>Audience</span><select style={inputStyle} value={audience} onChange={(e) => setAudience(e.target.value as IssuePayload['audience'])}>{AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}</select></label>
            <label><span style={fieldLabel}>Role</span><select style={inputStyle} value={role} onChange={(e) => setRole(e.target.value as IssuePayload['role'])}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></label>
            <label><span style={fieldLabel}>Max uses</span><input style={inputStyle} type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} /></label>
            <label><span style={fieldLabel}>Expires in</span><select style={inputStyle} value={expiresIn} onChange={(e) => setExpiresIn(e.target.value as ExpiresIn)}>{EXPIRES.map((x) => <option key={x} value={x}>{x}</option>)}</select></label>
          </div>
        </SectionCard>

        <SectionCard title="Bind to target" hint="A bound code needs a target id — no code without a destination. All 12 target types are live (migration 20260710000001 applied).">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label><span style={fieldLabel}>Target type</span>
              <select style={inputStyle} value={targetType} onChange={(e) => setTargetType(e.target.value as '' | AccessCodeTargetType)}>
                <option value="">No destination (unbound)</option>
                {SECURE_TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label><span style={fieldLabel}>Target id{targetType ? ' *' : ''}</span><input style={inputStyle} value={targetId} disabled={!targetType} onChange={(e) => setTargetId(e.target.value)} placeholder={targetType ? 'portal id / slug / ref' : '(pick a target type first)'} /></label>
            <label style={{ gridColumn: '1 / -1' }}><span style={fieldLabel}>Target label (optional)</span><input style={inputStyle} value={targetLabel} disabled={!targetType} onChange={(e) => setTargetLabel(e.target.value)} placeholder="human label shown in lists" /></label>
          </div>
        </SectionCard>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button type="button" disabled={!canSubmit} onClick={() => canSubmit && onSubmit?.({ label: label.trim(), audience, role, max_uses: Math.max(1, parseInt(maxUses, 10) || 1), expires_in: expiresIn, target_type: targetType || undefined, target_id: targetType ? targetId.trim() : undefined, target_label: targetType && targetLabel.trim() ? targetLabel.trim() : undefined })} style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>{saving ? 'Issuing…' : 'Issue access code'}</button>
          <button type="button" style={chipBtn} onClick={onOpenHub}>Cancel</button>
          {!label.trim() && <span style={{ fontSize: 11, color: CC_INK.faint }}>A label is required.</span>}
        </div>
      </div>
    </CommandCenterShell>
  )
}
